/**
 * Kamus Bahasa Indonesia (§H9).
 *
 * Kuncinya kalimat Inggrisnya sendiri — alasannya di `i18n/index.tsx`. Baris
 * yang belum ada di sini otomatis tampil dalam bahasa Inggris, jadi kamus yang
 * belum lengkap tidak pernah merusak layar.
 *
 * Dua aturan penerjemahan yang dipegang di seluruh berkas ini:
 *
 * 1. **Istilah teknis yang sudah jadi nama tetap Inggris**: dashboard, stream,
 *    substream, mainstream, playback, timeline, log, retention, bitrate, RTSP,
 *    HD. Menerjemahkan "substream" jadi "aliran bawah" hanya membuat orang yang
 *    memasang kamera harus menerjemahkannya balik untuk bicara dengan
 *    vendornya.
 * 2. **Bukan terjemahan harfiah, melainkan kalimat yang akan diucapkan orang**
 *    di ruang kontrol. "No cameras to show yet" jadi "Belum ada kamera", bukan
 *    "Tidak ada kamera untuk ditampilkan belum".
 */
export const KAMUS_ID: Record<string, string> = {
  // ——— Navigasi (§G9) ———
  Dashboard: 'Dashboard',
  'Custom Dashboards': 'Dashboard Sendiri',
  Cameras: 'Kamera',
  Recordings: 'Rekaman',
  Logs: 'Log',
  Users: 'Pengguna',
  'Camera Access': 'Akses Kamera',
  Settings: 'Pengaturan',
  Status: 'Status',
  About: 'Tentang',
  'Main navigation': 'Navigasi utama',
  'Open navigation': 'Buka navigasi',
  'Close navigation': 'Tutup navigasi',
  'Expand navigation': 'Lebarkan navigasi',
  'Collapse navigation': 'Ciutkan navigasi',
  'Skip to content': 'Lompat ke konten',
  'Lensa — home': 'Lensa — beranda',
  ' cameras need attention': ' kamera perlu diperiksa',

  // ——— Akun & tema ———
  Profile: 'Profil',
  'Sign out': 'Keluar',
  'Switch to User view': 'Lihat sebagai User',
  'Back to Admin view': 'Kembali ke mode Admin',
  '— open account menu': '— buka menu akun',
  'User view': 'Mode User',
  Administrator: 'Administrator',
  Operator: 'Operator',
  User: 'User',
  'Viewing as {nama} ({username})': 'Sedang memakai akun {nama} ({username})',
  'Switch back to {username}': 'Kembali ke {username}',
  'Signed in as {username}': 'Login sebagai {username}',
  'Light theme': 'Mode terang',
  'Dark theme': 'Mode gelap',
  'Match system': 'Ikut sistem',
  '{tema}. Click to change theme.': '{tema}. Klik untuk ganti tema.',
  Language: 'Bahasa',
  'English': 'English',
  'Bahasa Indonesia': 'Bahasa Indonesia',
  'Language. Click to switch.': 'Bahasa. Klik untuk mengganti.',

  // ——— Umum ———
  Save: 'Simpan',
  'Save changes': 'Simpan perubahan',
  Cancel: 'Batal',
  Delete: 'Hapus',
  Edit: 'Ubah',
  Close: 'Tutup',
  Previous: 'Sebelumnya',
  Next: 'Berikutnya',
  'Saving…': 'Menyimpan…',
  'Loading…': 'Memuat…',
  'Reconnecting…': 'Menyambung ulang…',
  Paused: 'Dijeda',
  Rows: 'Baris',
  'Rows per page': 'Baris per halaman',
  '{total} {noun}': '{total} {noun}',
  Pagination: 'Penomoran halaman',
  'First page': 'Halaman pertama',
  'Last page': 'Halaman terakhir',
  'Page {page}': 'Halaman {page}',
  'Page {page} of {pageCount} — {noun} {dari}–{sampai} of {total}':
    'Halaman {page} dari {pageCount} — {noun} {dari}–{sampai} dari {total}',
  cameras: 'kamera',
  'camera groups': 'grup kamera',
  users: 'pengguna',

  // ——— Status kamera (§G3.1) ———
  REC: 'REC',
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
  ERROR: 'ERROR',
  DISABLED: 'NONAKTIF',
  'Needs attention': 'Perlu diperiksa',
  'Limited quality': 'Kualitas terbatas',

  // ——— Tile & layar penuh ———
  'View {nama} fullscreen': 'Lihat {nama} layar penuh',
  '{nama} — fullscreen': '{nama} — layar penuh',
  '{nama} — recordings': '{nama} — rekaman',
  'Open recordings for {nama}': 'Buka rekaman {nama}',
  'Previous camera': 'Kamera sebelumnya',
  'Next camera': 'Kamera berikutnya',
  'Previous camera (←)': 'Kamera sebelumnya (←)',
  'Next camera (→)': 'Kamera berikutnya (→)',
  'Close (Esc)': 'Tutup (Esc)',
  'Stop (Esc)': 'Berhenti (Esc)',
  'This camera cannot provide an HD stream right now.':
    'Kamera ini sedang tidak bisa memberikan stream HD.',
  'The HD stream could not be started.': 'Stream HD gagal dijalankan.',
  'Limited quality — {pesan}': 'Kualitas terbatas — {pesan}',

  // ——— Dashboard (§G8) ———
  'Grid layout': 'Tata letak grid',
  Fullscreen: 'Layar penuh',
  Slideshow: 'Slideshow',
  'Slideshow interval': 'Jeda slideshow',
  'Slideshow controls': 'Kendali slideshow',
  'Page controls': 'Kendali halaman',
  'Pause slideshow': 'Jeda slideshow',
  'Resume slideshow': 'Lanjutkan slideshow',
  'Previous page': 'Halaman sebelumnya',
  'Next page': 'Halaman berikutnya',
  'No cameras': 'Tidak ada kamera',
  'No matching cameras': 'Tidak ada kamera yang cocok',
  'No cameras to show yet': 'Belum ada kamera',
  'Loading cameras…': 'Memuat kamera…',
  'Could not load cameras: {pesan}': 'Kamera gagal dimuat: {pesan}',
  'Leave monitor {nama}': 'Keluar dari dashboard {nama}',

  'Nothing matches “{cari}”. Try a different search.':
    'Tidak ada yang cocok dengan “{cari}”. Coba kata lain.',
  'Ask an administrator to add cameras to your dashboard.':
    'Minta administrator menambahkan kamera ke dashboard Anda.',

  // ——— Login ———
  'Sign in': 'Masuk',
  'Signing in…': 'Sedang masuk…',
  'Username or email': 'Username atau email',
  Password: 'Kata sandi',
  'Keep me signed in': 'Tetap masuk',
  'Sign in with your LENSA account.': 'Masuk dengan akun LENSA Anda.',
  'Need help?': 'Butuh bantuan?',
  'Trouble accessing your account?': 'Kesulitan mengakses akun?',
  'Contact the LENSA administrator in your organization for account help or a password reset.':
    'Hubungi administrator LENSA di organisasi Anda untuk bantuan akun atau pengaturan ulang kata sandi.',
  'Access based on your permissions': 'Akses sesuai izin Anda',
  'Choose “Keep me signed in” only on a personal device.':
    'Gunakan perangkat pribadi saat memilih “Tetap masuk”.',
  'Enter your username or email': 'Masukkan username atau email',
  'Enter your password': 'Masukkan kata sandi',
  'Show password': 'Tampilkan kata sandi',
  'Hide password': 'Sembunyikan kata sandi',
  'Secure • Reliable • Integrated': 'Aman • Andal • Terintegrasi',
  // Dua bentuk karena bahasa Inggris memisahkan tunggal & jamak; Bahasa
  // Indonesia tidak, jadi kedua entri di sini memang boleh mirip.
  'Try again in 1 minute.': 'Coba lagi dalam 1 menit.',
  'Try again in {menit} minutes.': 'Coba lagi dalam {menit} menit.',

  // Hero /login (§G18, teks paket Login Family). Headline dipecah dua kunci
  // karena kata terakhirnya diberi warna aksen; bentuk utuhnya dipakai mobile.
  'Monitor. Protect. Trust.': 'Pantau. Lindungi. Percaya.',
  'Monitor. Protect.': 'Pantau. Lindungi.',
  'Trust.': 'Percaya.',
  'An integrated video surveillance system for reliable, controlled security wherever you are.':
    'Sistem pengawasan video terintegrasi untuk keamanan yang andal dan terkendali di mana pun Anda berada.',
  Secure: 'Aman',
  'Access based on user rights': 'Akses sesuai hak pengguna',
  'Real-time': 'Real-time',
  'Live camera monitoring': 'Pantauan kamera langsung',
  Integrated: 'Terintegrasi',
  'Cameras in one view': 'Kamera dalam satu tampilan',
  Controlled: 'Terkendali',
  'Easier access management': 'Kelola akses lebih mudah',
  'One view. More peace of mind.': 'Satu tampilan. Lebih tenang.',

  // ——— Custom Dashboards (§F9) ———
  'Loading dashboards…': 'Memuat dashboard…',
  'New dashboard': 'Dashboard baru',
  'New dashboard…': 'Dashboard baru…',
  'Create dashboard': 'Buat dashboard',
  'Add to dashboard': 'Tambahkan ke dashboard',
  'No custom dashboards yet.': 'Belum ada dashboard sendiri.',
  Name: 'Nama',
  'Front gate at night': 'Gerbang depan malam hari',
  '{dipilih} of {total} cameras selected': '{dipilih} dari {total} kamera dipilih',
  'Search cameras': 'Cari kamera',
  'Search Name or Path': 'Cari Nama atau Path',
  'No cameras have been assigned to your account yet.':
    'Belum ada kamera yang ditugaskan ke akun Anda.',
  'No camera matches “{cari}”.': 'Tidak ada kamera yang cocok dengan “{cari}”.',
  'View only': 'Lihat saja',
  'No cameras have been assigned to your account yet, so there is nothing to build a dashboard from.':
    'Belum ada kamera yang ditugaskan ke akun Anda, jadi belum ada bahan untuk merakit dashboard.',
  'No custom dashboards yet. Build one from the cameras you already have.':
    'Belum ada dashboard sendiri. Rakit satu dari kamera yang sudah Anda punya.',
  '{n} cameras': '{n} kamera',
  'Removed camera': 'Kamera terhapus',
  '+{n} more': '+{n} lagi',
  Open: 'Buka',

  // ——— Halaman Cameras (§G11) ———
  Online: 'Online',
  Offline: 'Offline',
  Error: 'Error',
  '(disabled)': '(nonaktif)',
  'Search Name, Host or Path': 'Cari Nama, Host, atau Path',
  Sort: 'Urutkan',
  Size: 'Ukuran',
  'Name A→Z': 'Nama A→Z',
  'Name Z→A': 'Nama Z→A',
  'Problems first': 'Yang bermasalah dulu',
  List: 'Daftar',
  Small: 'Kecil',
  Medium: 'Sedang',
  Large: 'Besar',
  'Add camera': 'Tambah kamera',
  'No cameras yet. Add the first one.': 'Belum ada kamera. Tambahkan yang pertama.',
  'Clear search': 'Bersihkan pencarian',
  'Follows the global retention setting': 'Ikut setelan retensi global',
  '{n}d global': '{n}h global',
  'Live view': 'Lihat langsung',
  Playback: 'Playback',
  'View logs': 'Lihat log',
  'More options': 'Opsi lain',
  'Test main stream': 'Uji main stream',
  'Test sub stream': 'Uji sub stream',
  'Camera settings': 'Setelan kamera',
  'Testing connection…': 'Menguji koneksi…',
  Connected: 'Tersambung',
  at: 'pada',
  'with audio': 'dengan audio',
  'H.265 counts double against the streaming budget':
    'H.265 dihitung dua kali terhadap jatah streaming',
  Failed: 'Gagal',
  'Delete camera {nama}': 'Hapus kamera {nama}',
  'and its entire recording index will be permanently deleted. Recorded files already on disk are removed with it.':
    'beserta seluruh indeks rekamannya akan dihapus permanen. Berkas rekaman yang sudah ada di disk ikut dibuang.',
  Enter: 'Ketik',
  'below to confirm.': 'di bawah ini untuk mengonfirmasi.',
  'The name must match exactly.': 'Namanya harus sama persis.',
  'Enter your password to confirm.': 'Masukkan kata sandi Anda untuk mengonfirmasi.',
  'Your password is required.': 'Kata sandi wajib diisi.',
  'Deleting…': 'Menghapus…',
  'New dashboard containing {nama}:': 'Dashboard baru berisi {nama}:',

  // ——— Halaman Logs (§D7.3). Isi pesan log TIDAK diterjemahkan — lihat
  // alasannya di `i18n/index.tsx`; yang di bawah ini kerangkanya saja. ———
  'Event log': 'Log kejadian',
  'cameras are offline right now': 'kamera sedang offline',
  '(error)': '(error)',
  'cameras reported in the last {jam} hours': 'kamera melapor dalam {jam} jam terakhir',
  'Camera {id}': 'Kamera {id}',
  Camera: 'Kamera',
  'All cameras': 'Semua kamera',
  Level: 'Level',
  'Time range': 'Rentang waktu',
  'Last hour': '1 jam terakhir',
  'Last 24 hours': '24 jam terakhir',
  'Last 3 days': '3 hari terakhir',
  'Last 7 days': '7 hari terakhir',
  'Last 30 days': '30 hari terakhir',
  'Search messages': 'Cari pesan',
  'e.g. timeout': 'mis. timeout',
  'Export CSV': 'Ekspor CSV',
  'Loading events…': 'Memuat kejadian…',
  'No events in this time range.': 'Tidak ada kejadian di rentang waktu ini.',
  'Try widening the time range, or including more levels.':
    'Coba lebarkan rentang waktunya, atau tambahkan level lain.',
  'Time (WIB)': 'Waktu (WIB)',
  Message: 'Pesan',
  Count: 'Jumlah',
  since: 'sejak',
  'Technical details are available to administrators only.':
    'Detail teknis hanya untuk administrator.',
  'Page {page} of {pageCount} — {total} entries':
    'Halaman {page} dari {pageCount} — {total} baris',

  // ——— Halaman Recordings / playback (§G10) ———
  '— select a camera —': '— pilih kamera —',
  From: 'Dari',
  To: 'Sampai',
  Show: 'Tampilkan',
  'Choose a camera to see its recordings': 'Pilih kamera untuk melihat rekamannya',
  'No recordings in this range': 'Tidak ada rekaman di rentang ini',
  '{nama} has no recordings between {dari} and {sampai}. Try widening the range.':
    '{nama} tidak punya rekaman antara {dari} dan {sampai}. Coba lebarkan rentangnya.',
  'Fullscreen (F)': 'Layar penuh (F)',
  'Play the video fullscreen': 'Putar video layar penuh',
  'Exit fullscreen (F)': 'Keluar layar penuh (F)',
  'Leave fullscreen': 'Keluar dari layar penuh',
  'Playback position': 'Posisi pemutaran',
  'Playback controls': 'Kendali pemutaran',
  'Rewind (J)': 'Mundur (J)',
  Rewind: 'Mundur',
  'Rewinding {n}×': 'Mundur {n}×',
  'Fast forward (L)': 'Maju cepat (L)',
  'Fast forward': 'Maju cepat',
  'Forward {n}×': 'Maju {n}×',
  'Pause (Space)': 'Jeda (Spasi)',
  'Play (Space)': 'Putar (Spasi)',
  Pause: 'Jeda',
  Play: 'Putar',
  rewind: 'mundur',
  'Back to normal speed': 'Kembali ke laju normal',
  'Back to 1× (K stops instead)': 'Kembali ke 1× (K untuk berhenti)',
  'Playing at normal speed': 'Sedang berjalan pada laju normal',
  'Mark in (I)': 'Mark in (I)',
  'Mark out (O)': 'Mark out (O)',
  'Clear marks': 'Hapus mark',
  'Preparing file…': 'Menyiapkan berkas…',
  'Download {durasi}': 'Unduh {durasi}',
  'Help (?)': 'Bantuan (?)',
  'Keyboard shortcuts': 'Pintasan papan ketik',
  // Halaman Playback, revisi lapangan 31 Agu 2026 (§G10.4, §G10.5).
  'Hide filters': 'Sembunyikan filter',
  'Back to 1× (double-tap the picture works too)': 'Kembali ke 1× (ketuk ganda gambarnya juga bisa)',
  reset: 'atur ulang',
  // Durasi segmen global + inherit (§D2), 31 Agu 2026.
  'Recording segment length (minutes)': 'Panjang segmen rekaman (menit)',
  'Applies to the next segment — files being written now keep their length.':
    'Berlaku pada segmen berikutnya — berkas yang sedang ditulis selesai dengan panjang lamanya.',
  'Segment length (minutes)': 'Panjang segmen (menit)',
  'Following the global setting — {n} minutes.': 'Mengikuti setelan global — {n} menit.',
  '{n}m global': '{n}m global',
  'Clips ({n})': 'Klip ({n})',
  'Close the clip list': 'Tutup daftar klip',
  'Download this clip': 'Unduh klip ini',
  'Showing the first {n} clips — narrow the range to see the rest.':
    'Menampilkan {n} klip pertama — persempit rentangnya untuk melihat sisanya.',
  '{n} gaps': '{n} bolong',
  'Download from marks: {dari} – {sampai}': 'Unduh dari mark: {dari} – {sampai}',
  'Whole range': 'Seluruh rentang',
  '{durasi} of gaps will be skipped': '{durasi} bolong akan dilewati',
  'Downloads are capped at {jam} hours — narrow the range or use mark in/out.':
    'Unduhan dibatasi {jam} jam — persempit rentangnya atau pakai mark in/out.',
  'The file could not be prepared.': 'Berkasnya gagal disiapkan.',
  'Download again': 'Unduh lagi',
  'deleted 30 minutes after download': 'dihapus 30 menit setelah diunduh',
  'The start time must be before the end time.':
    'Waktu mulai harus lebih awal daripada waktu selesai.',

  // Peta tombol §G10.1
  'Play / pause': 'Putar / jeda',
  'Forward — press again for 2×, 4×, 8×': 'Maju — tekan lagi untuk 2×, 4×, 8×',
  'Rewind — press again for 2×, 4×, 8×': 'Mundur — tekan lagi untuk 2×, 4×, 8×',
  'Stop, back to 1×': 'Berhenti, kembali ke 1×',
  'Step one frame': 'Geser satu frame',
  'Step one second': 'Geser satu detik',
  'Zoom the timeline': 'Zoom timeline',
  // "Klip", mengikuti 'Next clip'/'Previous clip' yang sudah ada di bawah —
  // dua sebutan untuk benda yang sama di satu layar lebih membingungkan
  // daripada istilah mana pun yang dipilih.
  'Previous / next clip': 'Klip sebelumnya / berikutnya',
  'Next clip (PgDn)': 'Klip berikutnya (PgDn)',
  'Previous clip (PgUp)': 'Klip sebelumnya (PgUp)',
  'Clear mark in / mark out': 'Buang mark in / mark out',
  'To the visible edge — press again to page one screen':
    'Ke tepi yang terlihat — tekan lagi untuk pindah satu layar',

  // ——— Kendali skala timeline (8 Sep 2026) ———
  Scale: 'Skala',
  'Timeline scale': 'Skala timeline',
  // '1 minute' sudah ada di bagian jeda slideshow di bawah — terjemahannya
  // sama persis, jadi dipakai ulang daripada digandakan.
  '{n} minutes': '{n} menit',
  '1 hour': '1 jam',
  '{n} hours': '{n} jam',
  '1 day': '1 hari',
  'Mark in / mark out': 'Mark in / mark out',
  'Jump to mark in / out': 'Lompat ke mark in / out',
  'Drop a marker': 'Pasang penanda',
  'Next / previous marker': 'Penanda berikutnya / sebelumnya',
  'Clear mark in / out': 'Batalkan mark in / out',
  Playhead: 'Playhead',
  'No image': 'Tidak ada gambar',

  // ——— Panel kesehatan kamera (§G10.2, §D9.2) ———
  'Camera status is unavailable.': 'Status kamera tidak tersedia.',
  'Checking camera status…': 'Memeriksa status kamera…',
  'Frame lag': 'Jeda frame',
  'no data': 'tidak ada data',
  Bitrate: 'Bitrate',
  'not measured yet': 'belum terukur',
  'Coverage, last 24 h': 'Cakupan, 24 jam terakhir',
  'Coverage since start': 'Cakupan sejak mulai',
  Recording: 'Perekaman',
  'Hide details': 'Sembunyikan rincian',
  Details: 'Rincian',
  'Connected since': 'Tersambung sejak',
  'Estimated storage': 'Perkiraan storage',
  '{n} GB per day': '{n} GB per hari',
  Retention: 'Retensi',
  '{n} days': '{n} hari',
  'not recording': 'tidak merekam',
  'Recordings in this window': 'Rekaman di jendela ini',
  '{n} segments': '{n} segmen',
  Gaps: 'Bolong',
  none: 'tidak ada',
  'Last error': 'Error terakhir',
  'Recent events': 'Kejadian terakhir',
  'No events recorded.': 'Belum ada kejadian tercatat.',

  // ——— Formulir kamera (§G11) ———
  Host: 'Host',
  'IPv4, IPv6, or hostname': 'IPv4, IPv6, atau hostname',
  'RTSP port': 'Port RTSP',
  'Web UI port': 'Port web UI',
  'Used by the “Camera settings” button.': 'Dipakai tombol “Setelan kamera”.',
  Transport: 'Transport',
  'RTSP over TCP': 'RTSP lewat TCP',
  'RTSP over UDP': 'RTSP lewat UDP',
  'Main stream path': 'Path main stream',
  'e.g. /Streaming/Channels/101': 'mis. /Streaming/Channels/101',
  'Sub-stream path': 'Path sub-stream',
  'e.g. /channel=1&stream=1.sdp?': 'mis. /channel=1&stream=1.sdp?',
  'Camera username': 'Username kamera',
  'Camera password': 'Kata sandi kamera',
  'Saved. Leave empty to keep it unchanged.': 'Tersimpan. Kosongkan kalau tidak diganti.',
  'Stored encrypted, and never shown again.':
    'Disimpan terenkripsi, dan tidak pernah ditampilkan lagi.',
  Mode: 'Mode',
  'Live view plus continuous recording to disk.':
    'Lihat langsung sekaligus merekam terus-menerus ke disk.',
  'Live view only — nothing is written to disk, and retention below does not apply.':
    'Hanya lihat langsung — tidak ada yang ditulis ke disk, dan retensi di bawah tidak berlaku.',
  'View and Record': 'Lihat dan Rekam',
  Remarks: 'Catatan',
  'Notes about this camera that stay — wiring, quirks, who owns it. Shown on the camera list.':
    'Catatan yang menetap tentang kamera ini — kabel, kebiasaan rusaknya, siapa pemiliknya. Ikut tampil di daftar kamera.',
  'View Only': 'Lihat Saja',
  'Preview frame rate (fps)': 'Laju frame pratinjau (fps)',
  // ——— Playback multi-kamera (§G10.9) ———
  'Multi-camera': 'Multi-kamera',
  'Multi-Camera Playback': 'Playback Multi-Kamera',
  'One timeline. Several angles. The same second, seen from every side.':
    'Satu timeline. Banyak sudut. Detik yang sama, dilihat dari segala sisi.',
  'Single camera': 'Satu kamera',
  'Add cameras': 'Tambah kamera',
  'Four cameras at a time — remove one to add another.':
    'Empat kamera sekaligus — lepas satu untuk menambah yang lain.',
  'Pick up to four cameras to compare': 'Pilih sampai empat kamera untuk dibandingkan',
  'They all follow one clock, so the same second lines up across every angle.':
    'Semuanya mengikuti satu jam, jadi detik yang sama sejajar di semua sudut.',
  'No recording at this moment': 'Tidak ada rekaman pada detik ini',
  'Four angles at once need a wide screen. On a phone each camera would be barely taller than its own label — open this on a desktop or a tablet in landscape.':
    'Empat sudut sekaligus butuh layar lebar. Di ponsel tiap kamera cuma setinggi baris namanya sendiri — buka ini di desktop atau tablet mendatar.',
  'Open single-camera playback': 'Buka playback satu kamera',
  'Fit range': 'Muat rentang',
  'Step ten seconds': 'Geser sepuluh detik',
  'Show or hide this list': 'Tampilkan atau sembunyikan daftar ini',
  'Remove from view': 'Lepas dari tampilan',
  'Remove {nama} from this view': 'Lepas {nama} dari tampilan ini',

  // ——— Playback: pintu masuk & kendali (§G10, revisi 8 Sep 2026) ———
  Date: 'Tanggal',
  Today: 'Hari ini',
  'Quick range': 'Rentang cepat',
  'More filters': 'Filter lain',
  // "Lainnya" dan bukan "Selengkapnya": yang di balik tombol ini bukan
  // penjelasan yang lebih panjang, melainkan kendali yang jarang dipakai.
  More: 'Lainnya',
  'Playback speed': 'Kecepatan pemutaran',
  'Previous clip': 'Klip sebelumnya',
  'Next clip': 'Klip berikutnya',
  'Save this frame (PNG)': 'Simpan gambar ini (PNG)',
  'Save this frame as a picture': 'Simpan gambar ini sebagai berkas',
  '{n} fps': '{n} fps',
  // Legenda pita segmen. "Tidak ada rekaman" bukan "tidak ada kejadian" —
  // bedanya itu seluruh gunanya legenda ini ada.
  'No recording': 'Tidak ada rekaman',
  'Zoom out': 'Perkecil',
  'Zoom in': 'Perbesar',
  'Go to time': 'Ke jam',
  Go: 'Ke sana',
  'Earlier previews': 'Pratinjau sebelumnya',
  'Later previews': 'Pratinjau berikutnya',
  'Jump to {jam}': 'Lompat ke {jam}',
  'Generating thumbnails…': 'Membuat pratinjau…',
  // ——— Isi pratayang yang bolong (§D8.1, 8 Sep 2026) ———
  'Fill blank thumbnails': 'Isi pratinjau yang kosong',
  'Drag to resize the picture (double-click to reset)': 'Tarik untuk mengatur tinggi gambar (klik ganda untuk kembali)',
  'Drag to resize the timeline (double-click to reset)': 'Tarik untuk mengatur tebal timeline (klik ganda untuk kembali)',
  'Fill blank thumbnails — whole fleet': 'Isi pratinjau yang kosong — seluruh armada',
  'Timeline previews': 'Pratinjau Timeline',
  'Scan for missing thumbnails automatically': 'Cari pratinjau yang hilang secara otomatis',
  'Only works on cameras nobody is watching, and steps aside the moment someone opens their playback page. Off by default.':
    'Hanya bekerja pada kamera yang tidak sedang ditonton siapa pun, dan langsung mengalah begitu ada yang membuka halaman playback-nya. Bawaannya mati.',
  'Fills in preview thumbnails for recordings that do not have one yet. Recordings that already have a preview are never rebuilt.':
    'Membuatkan pratinjau untuk rekaman yang belum punya. Rekaman yang pratinjaunya sudah ada tidak pernah dibuat ulang.',
  'It runs slowly on purpose, a couple of previews at a time, so it never competes with recording. A full archive can take hours; you can leave this page.':
    'Sengaja berjalan pelan, dua pratinjau sekali jalan, supaya tidak pernah berebut dengan perekaman. Arsip penuh bisa makan berjam-jam; halaman ini boleh ditinggal.',
  'Another fill is already running.': 'Ada pengisian lain yang sedang berjalan.',
  '{n}% · {dibuat} created': '{n}% · {dibuat} dibuat',
  'Done — {dibuat} previews created, {dilewati} already had one.':
    'Selesai — {dibuat} pratinjau dibuat, {dilewati} sudah punya.',
  '{jam} — no recording': '{jam} — tidak ada rekaman',
  '{jam} — preview still being generated': '{jam} — pratinjaunya sedang dibuat',
  'No recording in this part of the timeline': 'Tidak ada rekaman di bagian timeline ini',
  'Retention (days)': 'Retensi (hari)',
  'Following the global setting — {n} days.': 'Ikut setelan global — {n} hari.',
  'Clear the box to follow the global setting instead.':
    'Kosongkan kotaknya untuk mengikuti setelan global.',
  // §D6 — batas 30 hari. Kalimatnya menyebut **siapa** yang menghapus, bukan
  // cuma angkanya: batas tanpa sebab terbaca sebagai kesewenangan aplikasi, dan
  // yang membacanya di lapangan berhak tahu bahwa rekamannya memang hilang di
  // hari ke-30 apa pun yang tertulis di layar ini.
  'At most {n} days — recordings older than that are removed by the storage server itself.':
    'Paling lama {n} hari — rekaman yang lebih tua dihapus sendiri oleh server penyimpanan.',
  '{n} days is the maximum. Anything longer cannot be kept.':
    'Maksimal {n} hari. Lebih dari itu tidak bisa disimpan.',
  '{n} (global)': '{n} (global)',
  'Segment length (seconds)': 'Panjang segmen (detik)',
  'Web UI uses HTTPS': 'Web UI pakai HTTPS',
  'Record audio': 'Rekam audio',
  Enabled: 'Aktif',

  // ——— Halaman Users (§C2.1, §F7.1) ———
  'Loading users…': 'Memuat pengguna…',
  Username: 'Username',
  Source: 'Sumber',
  Role: 'Peran',
  'Last sign-in': 'Login terakhir',
  Actions: 'Aksi',
  inactive: 'nonaktif',
  locked: 'terkunci',
  active: 'aktif',
  'Reset password': 'Reset kata sandi',
  Unlock: 'Buka kunci',
  Deactivate: 'Nonaktifkan',
  Activate: 'Aktifkan',
  'Linked accounts': 'Akun tertaut',
  'Delete {username}? This cannot be undone.':
    'Hapus {username}? Tindakan ini tidak bisa dibatalkan.',
  'Add a local user': 'Tambah pengguna lokal',
  'Full name': 'Nama lengkap',
  'Email (optional)': 'Email (opsional)',
  Add: 'Tambah',
  '{username} can switch into these without a password. Coming back always asks for theirs.':
    '{username} bisa masuk ke akun-akun ini tanpa kata sandi. Untuk kembali, kata sandinya selalu diminta.',
  'There are no active viewer accounts to link.': 'Tidak ada akun User aktif untuk ditautkan.',
  'Save links': 'Simpan tautan',
  'New password for {username} (at least {n} characters):':
    'Kata sandi baru untuk {username} (minimal {n} karakter):',
  'Password changed for {username}. All of their sessions were signed out.':
    'Kata sandi {username} diganti. Semua sesinya dikeluarkan.',
  'Role updated for {username}.': 'Peran {username} diperbarui.',
  '{username} was unlocked.': '{username} dibuka kuncinya.',
  '{username} was deactivated.': '{username} dinonaktifkan.',
  '{username} was activated.': '{username} diaktifkan.',
  '{username} was deleted.': '{username} dihapus.',
  'User created.': 'Pengguna dibuat.',

  // ——— Halaman Camera Access (§C2.1) ———
  'Select all': 'Pilih semua',
  Clear: 'Kosongkan',
  'Select these': 'Pilih yang ini',
  'Clear these': 'Lepas yang ini',
  'Selected only': 'Yang dipilih saja',
  '{n} match': '{n} cocok',
  '{n} people': '{n} orang',
  'No camera has been picked yet.': 'Belum ada kamera yang dipilih.',
  'Add people…': 'Tambah orang…',
  'Nobody yet.': 'Belum ada.',
  'Remove {nama}': 'Lepas {nama}',
  'Type a name or username': 'Ketik nama atau username',
  'Nobody matches “{cari}”.': 'Tidak ada yang cocok dengan “{cari}”.',
  'Type a name; the list is there to scroll through if you would rather look.':
    'Ketik namanya; daftarnya tetap ada untuk digulung kalau lebih suka melihat.',
  Done: 'Selesai',
  'Delete camera group {nama}': 'Hapus grup kamera {nama}',
  'Everyone assigned to it loses access to its cameras at once. The cameras and their recordings are not touched.':
    'Semua orang yang ditugaskan padanya langsung kehilangan akses ke kameranya. Kamera dan rekamannya sendiri tidak tersentuh.',
  '{nama} was deleted.': '{nama} dihapus.',
  'Edit {nama}': 'Ubah {nama}',
  'New camera group': 'Grup kamera baru',
  Owner: 'Pemilik',
  'Use the dashboard default (60s)': 'Pakai bawaan dashboard (60d)',
  'No cameras have been added yet.': 'Belum ada kamera yang ditambahkan.',
  'Who can see it': 'Siapa yang boleh melihat',
  '(owner)': '(pemilik)',
  'No users yet.': 'Belum ada pengguna.',
  'Camera group created.': 'Grup kamera dibuat.',
  'Camera group saved.': 'Grup kamera disimpan.',
  'Delete “{nama}”?': 'Hapus “{nama}”?',
  'No camera groups yet': 'Belum ada grup kamera',
  'owner {nama}': 'pemilik {nama}',
  'slideshow {n}s': 'slideshow {n}d',
  'Not assigned to anyone': 'Belum ditugaskan ke siapa pun',
  'Seen by {daftar}': 'Dilihat oleh {daftar}',

  // ——— Konfirmasi hapus, dipakai seluruh aplikasi (§G18) ———
  'Delete tag {nama}': 'Hapus tag {nama}',
  'No camera uses it.': 'Tidak ada kamera yang memakainya.',
  '{n} cameras will lose this tag. The cameras themselves are not touched.':
    '{n} kamera kehilangan tag ini. Kameranya sendiri tidak tersentuh.',
  'Delete template {nama}': 'Hapus template {nama}',
  'No camera is changed — templates are only used when a camera is created.':
    'Tidak ada kamera yang berubah — template hanya dipakai saat kamera dibuat.',
  'Delete dashboard {nama}': 'Hapus dashboard {nama}',
  'The layout is deleted. The cameras in it are not touched — only this arrangement is gone.':
    'Susunannya dihapus. Kamera di dalamnya tidak tersentuh — yang hilang cuma susunan ini.',
  'Delete {username}': 'Hapus {username}',
  'The account, its sessions, and everything it owns personally — bookmarks and custom dashboards — are gone. Recordings and audit history stay.':
    'Akunnya, seluruh sesinya, dan semua yang dimilikinya sendiri — bookmark dan dashboard rakitannya — ikut hilang. Rekaman dan jejak audit tetap ada.',
  'Remove your photo': 'Hapus foto Anda',
  'Your initials take its place. A new photo can be uploaded any time.':
    'Inisial Anda menggantikannya. Foto baru bisa diunggah kapan saja.',
  'Remove this download': 'Hapus unduhan ini',
  'The file is already gone; this removes the row from the list.':
    'Berkasnya memang sudah tidak ada; ini menghapus barisnya dari daftar.',
  'The prepared file is deleted from the server. If the recording it came from has passed retention, this may be the only copy left.':
    'Berkas yang sudah jadi dihapus dari server. Kalau rekaman asalnya sudah lewat masa simpan, ini bisa jadi satu-satunya salinan yang tersisa.',
  'The recording itself is not touched — only this marker and the time it points at.':
    'Rekamannya sendiri tidak tersentuh — yang hilang cuma penanda ini dan titik waktunya.',

  // ——— Halaman Status ———
  'System status': 'Status sistem',
  'Cannot reach the API: {pesan}': 'API tidak bisa dihubungi: {pesan}',
  Total: 'Total',
  Faulty: 'Bermasalah',
  '{n} disabled': '{n} nonaktif',
  'Lensa component status': 'Status komponen Lensa',
  Component: 'Komponen',
  OK: 'OK',
  'Database (MariaDB)': 'Database (MariaDB)',
  'Recording worker': 'Worker perekaman',
  'Recording storage': 'Storage rekaman',
  Version: 'Versi',
  Environment: 'Lingkungan',
  'Hardware acceleration': 'Akselerasi hardware',
  'API uptime': 'Uptime API',
  Service: 'Layanan',
  CPU: 'CPU',
  Processor: 'Prosesor',
  '{n} cores': '{n} core',
  Usage: 'Pemakaian',
  'Load average': 'Load average',
  'ffmpeg processes': 'Proses ffmpeg',
  'Host uptime': 'Uptime server',

  // ——— Halaman Profile (§F7, §F7.1) ———
  'Profile could not be loaded.': 'Profil gagal dimuat.',
  Photo: 'Foto',
  'Change photo': 'Ganti foto',
  'Upload photo': 'Unggah foto',
  Remove: 'Hapus',
  'That file could not be read.': 'Berkas itu tidak bisa dibaca.',
  'Photo updated.': 'Foto diperbarui.',
  'Photo removed.': 'Foto dihapus.',
  'Saved.': 'Tersimpan.',
  Email: 'Email',
  Phone: 'Telepon',
  Address: 'Alamat',
  'Save details': 'Simpan data',
  'The two new passwords do not match.': 'Dua kata sandi barunya tidak sama.',
  'This account signs in with LDAP. Your password is managed in the directory, not here.':
    'Akun ini login lewat LDAP. Kata sandinya diatur di direktori, bukan di sini.',
  'Current password': 'Kata sandi sekarang',
  'New password': 'Kata sandi baru',
  'Repeat new password': 'Ulangi kata sandi baru',
  'Changing…': 'Mengganti…',
  'Change password': 'Ganti kata sandi',
  Access: 'Akses',
  'Cameras you can view': 'Kamera yang bisa Anda lihat',
  Dashboards: 'Dashboard',
  'All dashboards': 'Semua dashboard',
  'None assigned yet': 'Belum ada yang ditugaskan',
  'Ask an administrator to change any of this.':
    'Minta administrator kalau ada yang perlu diubah.',
  'Switch account': 'Pindah akun',
  'You are signed in as someone else. Enter the password for':
    'Anda sedang memakai akun orang lain. Masukkan kata sandi',
  'to go back.': 'untuk kembali.',
  'Switch back': 'Kembali',
  'Open one of these accounts to see exactly what they see. No password needed going in — coming back asks for yours.':
    'Buka salah satu akun ini untuk melihat persis apa yang dia lihat. Masuknya tidak perlu kata sandi — untuk kembali, kata sandi Anda diminta.',
  'View as': 'Lihat sebagai',

  // ——— Halaman Settings (§D5.1, §D6) ———
  'System settings': 'Pengaturan sistem',
  'Settings saved.': 'Pengaturan tersimpan.',
  'Settings could not be loaded. Retrying every 30 seconds.':
    'Pengaturan gagal dimuat. Dicoba lagi tiap 30 detik.',
  'Loading settings…': 'Memuat pengaturan…',
  'Storage forecast': 'Proyeksi storage',
  'No measurements yet': 'Belum ada pengukuran',
  Table: 'Tabel',
  Console: 'Konsol',
  'Retention & capacity': 'Retensi & kapasitas',
  'Global retention (days)': 'Retensi global (hari)',
  'Used by cameras that do not set their own.':
    'Dipakai kamera yang tidak menyetel sendiri.',
  'Maximum disk capacity (GB)': 'Kapasitas disk maksimum (GB)',
  'not limited': 'tidak dibatasi',
  'Oldest recordings are deleted above {n}%.':
    'Di atas {n}%, rekaman terlama dihapus duluan.',
  'Leave empty to use the whole volume.': 'Kosongkan untuk memakai seluruh volume.',
  '{dipakai} GB of {jatah} GB used': '{dipakai} GB dari {jatah} GB terpakai',
  '— above {n}%, the oldest recordings are being deleted':
    '— di atas {n}%, rekaman terlama sedang dihapus',
  '{n}% of the assigned disk capacity used': '{n}% dari jatah disk terpakai',
  'Recording folder': 'Folder rekaman',
  'Camera log retention': 'Retensi log kamera',
  'Keep logs for (days)': 'Simpan log selama (hari)',
  'Reset to default': 'Kembalikan ke bawaan',
  GPU: 'GPU',
  Card: 'Kartu',
  driver: 'driver',
  Memory: 'Memori',
  '{dipakai} MB of {total} MB used ({pct}%)': '{dipakai} MB dari {total} MB terpakai ({pct}%)',
  Temperature: 'Suhu',
  Utilization: 'Utilisasi',
  '{gpu}% GPU, {dec}% decoder': '{gpu}% GPU, {dec}% decoder',
  Power: 'Daya',
  'Read-only configuration': 'Konfigurasi baca-saja',
  mode: 'mode',
  device: 'device',
  fallback: 'fallback',
  on: 'aktif',
  off: 'mati',
  currently: 'sekarang',
  Streaming: 'Streaming',
  '{n}s idle timeout, up to {maks} concurrent streams':
    'idle timeout {n}d, maksimum {maks} stream bersamaan',
  LDAP: 'LDAP',
  enabled: 'aktif',
  disabled: 'nonaktif',
  '{mode} mode': 'mode {mode}',
  'Audit log': 'Log audit',
  'No activity recorded yet': 'Belum ada aktivitas tercatat',
  'No activity recorded yet.': 'Belum ada aktivitas tercatat.',
  Action: 'Aksi',
  Object: 'Objek',
  IP: 'IP',
  '{perHari} GB/day · {proyeksi} GB forecast · {n} cameras':
    '{perHari} GB/hari · proyeksi {proyeksi} GB · {n} kamera',
  '{proyeksi} GB forecast — over the {sisa} GB free':
    'proyeksi {proyeksi} GB — melebihi {sisa} GB yang tersisa',
  '{n} most recent entries — last {waktu}': '{n} baris terakhir — terbaru {waktu}',
  'Checking your session…': 'Memeriksa sesi Anda…',
  'Every camera, one screen.': 'Semua kamera, satu layar.',
  'Recording around the clock. Review any moment, from anywhere on your network.':
    'Merekam sepanjang waktu. Tinjau kapan pun, dari mana pun di jaringan Anda.',
  'JPG or PNG. The image is cropped to a square and stored at 256×256.':
    'JPG atau PNG. Gambarnya dipotong jadi bujur sangkar dan disimpan 256×256.',

  // ——— Camera Tags (§G11.1) ———
  Tags: 'Tag',
  'Tags: {n}': 'Tag: {n}',
  'Manage tags': 'Kelola tag',
  'Cameras must have every selected tag.': 'Kamera harus punya semua tag yang dipilih.',
  'Clear tag filter': 'Hapus saringan tag',
  'No tags have been created yet.': 'Belum ada tag yang dibuat.',
  'No camera has all of these tags: {daftar}.':
    'Tidak ada kamera yang punya semua tag ini: {daftar}.',
  'Tags group cameras. They never change who can see a camera.':
    'Tag menggolongkan kamera. Tag tidak pernah mengubah siapa yang boleh melihat kamera.',
  'Grouping only — tags never change who can see this camera.':
    'Sekadar penggolongan — tag tidak mengubah siapa yang boleh melihat kamera ini.',
  'New tag': 'Tag baru',
  Colour: 'Warna',
  'Colour of {name}': 'Warna {name}',
  'Delete tag': 'Hapus tag',
  'Delete “{name}”? No camera uses it.': 'Hapus “{name}”? Belum ada kamera yang memakainya.',
  'Delete “{name}”? {n} cameras will lose this tag. The cameras themselves are not touched.':
    'Hapus “{name}”? {n} kamera akan kehilangan tag ini. Kameranya sendiri tidak tersentuh.',

  // ——— Configuration Template (§G11.4) ———
  'Camera templates': 'Template kamera',
  'Repeated settings, stored once — used when a camera is created':
    'Setelan berulang, disimpan sekali — dipakai saat kamera dibuat',
  'A template fills in a new camera form. It is a starting point, not a link — editing a template never changes cameras that already exist.':
    'Template mengisi form kamera baru. Ia titik awal, bukan tautan — menyunting template tidak pernah mengubah kamera yang sudah ada.',
  'No templates yet.': 'Belum ada template.',
  'New template': 'Template baru',
  'Delete template': 'Hapus template',
  'Template name': 'Nama template',
  Description: 'Keterangan',
  'Tick a setting to put it in the template. Anything unticked is left alone when the template is applied.':
    'Centang setelan yang mau dimasukkan ke template. Yang tidak dicentang tidak disentuh saat template diterapkan.',
  '{n} settings': '{n} setelan',
  '{n} settings · credentials stored': '{n} setelan · kredensial tersimpan',
  'Delete “{name}”? No camera is changed — templates are only used when a camera is created.':
    'Hapus “{name}”? Tidak ada kamera yang berubah — template cuma dipakai saat kamera dibuat.',
  'Start from a template': 'Mulai dari template',
  'None — fill in everything by hand': 'Tanpa template — isi sendiri semuanya',
  'includes credentials': 'berikut kredensial',
  'The template fills the fields below. Change anything you need before saving.':
    'Template mengisi kolom di bawah. Ubah apa pun yang perlu sebelum disimpan.',
  'Stored — type to replace': 'Tersimpan — ketik untuk mengganti',
  'Not set': 'Belum diisi',
  'Stored encrypted. It is never sent back to the browser.':
    'Disimpan terenkripsi. Tidak pernah dikirim balik ke browser.',
  'RTSP transport': 'Transport RTSP',
  'Sub stream path': 'Path substream',
  'Empty means this camera has no sub stream.': 'Kosong berarti kamera ini tidak punya substream.',
  'Empty means no username.': 'Kosong berarti tanpa username.',
  'Record (View and Record)': 'Merekam (View and Record)',
  'Empty means it follows the global retention setting.':
    'Kosong berarti ikut setelan retensi global.',
  'Empty means it follows the global retention setting. At most 30 days.':
    'Kosong berarti ikut setelan retensi global. Paling lama 30 hari.',
  'Empty means it follows the global segment setting.':
    'Kosong berarti ikut setelan segmen global.',
  On: 'Aktif',

  // ——— Bulk Operation (§G11.2) ———
  'Select {name}': 'Pilih {name}',
  '{n} selected': '{n} dipilih',
  'Select all {n} matching the current filter': 'Pilih semua {n} yang cocok saringan sekarang',
  'Clear selection': 'Batalkan pilihan',
  'Choose an action…': 'Pilih tindakan…',
  'Change retention': 'Ubah retensi',
  'Enable / disable recording': 'Aktifkan / matikan perekaman',
  'Add tags': 'Pasang tag',
  'Remove tags': 'Lepas tag',
  'Apply template': 'Terapkan template',
  'Test connection': 'Uji koneksi',
  'Restart recorder': 'Restart recorder',
  'Follow the global retention setting': 'Ikut setelan retensi global',
  Days: 'Hari',
  'Choose a template…': 'Pilih template…',
  nothing: 'tidak ada',
  'Overwrites only these settings: {daftar}. Credentials are never applied to existing cameras.':
    'Hanya menimpa setelan ini: {daftar}. Kredensial tidak pernah diterapkan ke kamera yang sudah ada.',
  'This restarts the recorder on {n} cameras. The last few seconds of the segment being written are lost on each one — run it just after :00, :15, :30 or :45.':
    'Ini me-restart recorder di {n} kamera. Beberapa detik terakhir segmen yang sedang ditulis hilang di masing-masing — jalankan tepat sesudah menit :00, :15, :30, atau :45.',
  'Runs 8 at a time — cameras limit how many RTSP sessions they will accept at once.':
    'Dijalankan 8 sekaligus — kamera membatasi berapa sesi RTSP yang mau diterimanya bersamaan.',
  'Working…': 'Sedang dikerjakan…',
  'Apply to {n} cameras': 'Terapkan ke {n} kamera',
  '{ok} succeeded, {fail} failed.': '{ok} berhasil, {fail} gagal.',
  '{n} failed': '{n} gagal',
  '{n} succeeded': '{n} berhasil',
  'Retry only the {n} that failed': 'Ulangi hanya {n} yang gagal',

  // ——— Maintenance mode (§D10) ———
  MAINTENANCE: 'PEMELIHARAAN',
  Maintenance: 'Pemeliharaan',
  'Maintenance mode': 'Mode pemeliharaan',
  'Mark as maintenance': 'Tandai pemeliharaan',
  'End maintenance': 'Selesaikan pemeliharaan',
  'Mark / end maintenance': 'Tandai / selesaikan pemeliharaan',
  'What is being done to this camera?': 'Apa yang sedang dikerjakan pada kamera ini?',
  'Replace camera…': 'Ganti perangkat…',
  'Replace camera': 'Ganti perangkat',
  'Replace {nama}': 'Ganti perangkat {nama}',
  'The name, the recording folder and every existing recording stay with this camera. Only how Lensa reaches the device changes.':
    'Nama, folder rekaman, dan seluruh rekaman yang sudah ada tetap milik kamera ini. Yang berubah cuma cara Lensa menjangkau perangkatnya.',
  'This camera is not marked as under maintenance. Mark it first so the gap in its recording has a reason written down.':
    'Kamera ini belum ditandai dalam pemeliharaan. Tandai dulu supaya jeda rekamannya punya sebab yang tercatat.',
  'Save and test': 'Simpan lalu uji',
  'Save without testing': 'Simpan tanpa menguji',
  'Connected.': 'Tersambung.',
  stored: 'tersimpan',
  'not set': 'belum diisi',
  'Leave blank to keep the current one': 'Kosongkan untuk mempertahankan yang sekarang',
  Application: 'Aplikasi',
  Storage: 'Storage',
  'Lensa is being deployed or reworked. Recording keeps running — only the pages close, and only for non-admins.':
    'Lensa sedang di-deploy atau dikerjakan ulang. Perekaman tetap jalan — yang ditutup cuma halamannya, dan cuma untuk non-admin.',
  'The recording volume is being worked on. Recording stops on purpose; live view keeps working, and the gap gets a reason written into every camera’s log.':
    'Volume rekaman sedang dikerjakan. Perekaman berhenti dengan sengaja; gambar langsung tetap jalan, dan jedanya dicatat sebagai sebab di log tiap kamera.',
  'What is being done': 'Apa yang sedang dikerjakan',
  'e.g. Migrating the recording volume to the new NAS':
    'mis. Memindahkan volume rekaman ke NAS baru',
  'Everyone sees this sentence. “Under maintenance, try again later” is what makes people phone you.':
    'Kalimat ini dilihat semua orang. “Sedang pemeliharaan, coba lagi nanti” justru yang membuat orang menelepon Anda.',
  'Start maintenance': 'Mulai pemeliharaan',
  'Application maintenance is on.': 'Pemeliharaan aplikasi sedang menyala.',
  'Storage maintenance is on — recording is paused on purpose.':
    'Pemeliharaan storage sedang menyala — perekaman dihentikan dengan sengaja.',
  'Lensa is under maintenance.': 'Lensa sedang dalam pemeliharaan.',
  'Storage maintenance — recording is paused on purpose.':
    'Pemeliharaan storage — perekaman dihentikan dengan sengaja.',
  'since {waktu}, by {siapa}': 'sejak {waktu}, oleh {siapa}',

  // ——— Antrean export & Download Center (§D8.8, §D8.9, §G10.8, §G12) ———
  Downloads: 'Unduhan',
  'Download Center': 'Pusat Unduhan',
  'File name (optional)': 'Nama berkas (opsional)',
  'Export file name': 'Nama berkas export',
  'Untitled export': 'Export tanpa nama',
  'Waiting — number {n} in the queue': 'Menunggu giliran — nomor {n}',
  'Waiting its turn': 'Menunggu giliran',
  'starting…': 'memulai…',
  'No exports yet.': 'Belum ada export.',
  'Create one from the Video page — pick a range, then press Download.':
    'Buat dari halaman Video — pilih rentang, lalu tekan Unduh.',
  'Go to Video': 'Ke halaman Video',
  queued: 'antre',
  running: 'berjalan',
  ready: 'siap',
  failed: 'gagal',
  cancelled: 'dibatalkan',
  'file deleted {sisa}': 'berkas dihapus {sisa}',
  'file deleted on {waktu}': 'berkas sudah dihapus {waktu}',
  'file already deleted': 'berkas sudah dihapus',
  'in {n}h': 'dalam {n} jam',
  'in {n} min': 'dalam {n} menit',
  'any moment now': 'sebentar lagi',
  'Export again': 'Export ulang',
  'History is kept for 7 days.': 'Riwayat disimpan 7 hari.',

  // ——— Bookmark rekaman (§D8.7, §G10.7) ———
  'Bookmarks ({n})': 'Bookmark ({n})',
  'Bookmark at {jam}': 'Bookmark pada {jam}',
  'e.g. White car enters': 'mis. Mobil putih masuk',
  'Save bookmark': 'Simpan bookmark',
  'Close the bookmark list': 'Tutup daftar bookmark',
  'No bookmarks in this range. Press M while watching to add one.':
    'Belum ada bookmark di rentang ini. Tekan M saat menonton untuk menambahkan.',
  'recording past its retention': 'rekaman sudah lewat retensi',
  'Rename bookmark': 'Ganti label bookmark',
  'Delete bookmark': 'Hapus bookmark',
  'Delete this bookmark?': 'Hapus bookmark ini?',
  'Add a bookmark here': 'Pasang bookmark di sini',
  'Next / previous bookmark': 'Bookmark berikutnya / sebelumnya',

  // ——— Wall Status & Daily Health Summary (§D12, §G14) ———
  'Show system status': 'Tampilkan status sistem',
  'Hide system status': 'Sembunyikan status sistem',
  'All healthy': 'Semua sehat',
  '{n} need attention': '{n} perlu ditengok',
  yours: 'milik Anda',
  '{n} healthy': '{n} sehat',
  '{n} warning': '{n} waspada',
  '{n} problem': '{n} bermasalah',
  '{n} in maintenance': '{n} dalam pemeliharaan',
  'recording {n}%': 'rekaman {n}%',
  'disk {n}%': 'disk {n}%',
  '~{n} days left': 'sisa ±{n} hari',
  '{n} open incidents': '{n} catatan terbuka',

  // ——— Incident Notes (§D11) ———
  'Add an incident note': 'Tambah catatan pekerjaan',
  'What happened? e.g. PoE switch on floor 2 replaced':
    'Apa yang terjadi? mis. PoE switch lantai 2 diganti',
  'Details (optional)': 'Keterangan (opsional)',
  'Save note': 'Simpan catatan',
  'Mark as finished': 'Tandai selesai',
  ongoing: 'berlangsung',
  'until {jam}': 'sampai {jam}',

  // ——— Konteks kamera padam (§D9.3) ———
  'Last recorded': 'Terakhir merekam',
  Reconnect: 'Menyambung ulang',
  'Last bitrate': 'Bitrate terakhir',
  'Never connected yet.': 'Belum pernah tersambung.',
  'attempt {n}': 'percobaan ke-{n}',
  'attempt {n}, next in {s}s': 'percobaan ke-{n}, berikutnya {s} detik lagi',
  '{n} Mbps while it was up': '{n} Mbps saat masih hidup',
  'just now': 'baru saja',
  '{n} min ago': '{n} menit lalu',
  '{n}h ago': '{n} jam lalu',
  '{n}d ago': '{n} hari lalu',

  // ——— Bulk Import (§G11.3) ———
  Import: 'Impor',
  'Import cameras': 'Impor kamera',
  Upload: 'Unggah',
  Validate: 'Validasi',
  Preview: 'Pratinjau',
  Test: 'Uji',
  'Up to {n} cameras at a time. The file is read in your browser — it is never uploaded.':
    'Paling banyak {n} kamera sekali jalan. Berkasnya dibaca di browser Anda — tidak pernah diunggah.',
  'Choose a CSV file': 'Pilih berkas CSV',
  'Download a sample file': 'Unduh berkas contoh',
  'Start from an empty table instead': 'Atau mulai dari tabel kosong',
  'These columns were not recognised and will be ignored: {daftar}':
    'Kolom ini tidak dikenal dan akan diabaikan: {daftar}',
  'A CSV holding camera passwords is a file that gets left in someone’s Downloads folder. Put the credentials in a template instead and name it in the “template” column.':
    'CSV berisi password kamera adalah berkas yang akan tertinggal di folder Unduhan seseorang. Taruh kredensialnya di template, lalu sebut namanya di kolom “template”.',
  'That file has {n} rows — the limit is {max}.':
    'Berkas itu berisi {n} baris — batasnya {max}.',
  'The file has no data rows.': 'Berkas itu tidak punya baris data.',
  'The file needs at least a “name” and a “host” column.':
    'Berkasnya harus punya setidaknya kolom “name” dan “host”.',
  'Checking…': 'Memeriksa…',
  'Check {n} rows': 'Periksa {n} baris',
  'Check again': 'Periksa ulang',
  '{ok} rows are ready, {bad} need fixing.': '{ok} baris siap, {bad} perlu diperbaiki.',
  Issues: 'Keberatan',
  row: 'baris',
  '+ Add a row': '+ Tambah baris',
  'Preview {n} cameras': 'Pratinjau {n} kamera',
  Back: 'Kembali',
  global: 'global',
  set: 'terisi',
  '{n} rows are excluded because they still have errors.':
    '{n} baris dikeluarkan karena masih bermasalah.',
  'Next: test the connections': 'Lanjut: uji koneksinya',
  'Optional. Cameras are often registered before the cabling is finished — you can import without testing.':
    'Opsional. Kamera sering didaftarkan sebelum kabelnya selesai — impor tetap bisa dijalankan tanpa uji.',
  'Testing…': 'Menguji…',
  'Test {n} cameras': 'Uji {n} kamera',
  'Import {n} cameras': 'Impor {n} kamera',
  '{n} cameras created, {bad} failed.': '{n} kamera dibuat, {bad} gagal.',
  'Download the failed rows as CSV': 'Unduh baris yang gagal sebagai CSV',
  'Go to Cameras': 'Ke halaman Kamera',
  'Import another file': 'Impor berkas lain',
  'Recognised columns: {daftar}': 'Kolom yang dikenal: {daftar}',

  // ——— Camera Activity Timeline (§D7.4, §G13) ———
  'Log view': 'Tampilan log',
  Activity: 'Aktivitas',
  Technical: 'Teknis',
  'Pick a camera to see what happened to it.':
    'Pilih kamera untuk melihat apa yang terjadi padanya.',
  'The cards above show which cameras need looking at.':
    'Kartu di atas menunjukkan kamera mana yang perlu ditengok.',
  'Loading activity…': 'Memuat aktivitas…',
  Disconnected: 'Terputus',
  Reconnecting: 'Menyambung ulang',
  Problem: 'Bermasalah',
  'Under maintenance': 'Dalam pemeliharaan',
  Unstable: 'Tidak stabil',
  Event: 'Kejadian',
  '{n} attempts': '{n} percobaan',
  '{n} changes': '{n} pergantian',
  'ongoing, {lama}': 'berlangsung, {lama}',
  'Started before this range, on {tgl} {jam}':
    'Sudah berlangsung sebelum rentang ini, sejak {tgl} {jam}',
  'Show the changes': 'Lihat pergantiannya',
  'Hide the changes': 'Sembunyikan pergantiannya',
  'Open the technical log at this time': 'Buka log teknis pada waktu ini',
  'Nothing changed in this range.': 'Tidak ada pergantian pada rentang ini.',
  '{keadaan} without a break since {tgl} {jam} — {lama}.':
    '{keadaan} terus sejak {tgl} {jam} — {lama}.',
  'This camera was added on {tgl} and has not connected yet.':
    'Kamera ini ditambahkan {tgl} dan belum pernah tersambung.',
  'This range has more events than can be shown. Narrow the time range.':
    'Kejadian pada rentang ini lebih banyak daripada yang bisa ditampilkan. Persempit rentang waktunya.',
  // Satuan lama — dipakai Activity Timeline. Sengaja pendek: ia kolom yang
  // dipindai mata, bukan kalimat yang dibaca.
  '{n}s': '{n}d',
  '{n} min': '{n} mnt',
  '{n}h': '{n} jam',
  '{h}h {m}m': '{h} jam {m} mnt',
  '{n}d': '{n} hari',
  '{d}d {h}h': '{d} hari {h} jam',

  // ——— Halaman About (§G15) ———
  Product: 'Produk',
  'Video Surveillance': 'Video Surveillance',
  'Published by': 'Diterbitkan oleh',
  'Open {situs} in a new tab': 'Buka {situs} di tab baru',
  Support: 'Dukungan',
  Licence: 'Lisensi',
  'Proprietary Software — Free to Use': 'Perangkat Lunak Berpemilik — Gratis Digunakan',
  'Terms and conditions apply': 'Syarat dan ketentuan berlaku.',
  'About this application': 'Tentang aplikasi ini',
  'LENSA is a video surveillance platform for centralised monitoring, recording, playback, and camera management.':
    'LENSA adalah platform video surveillance untuk pemantauan, perekaman, pemutaran kembali, dan pengelolaan kamera secara terpusat.',
  'Third-party components': 'Komponen pihak ketiga',
  'Called as a separate process, not linked into Lensa.':
    'Dipanggil sebagai proses terpisah, tidak ditautkan ke dalam Lensa.',
  'Interface font.': 'Font antarmuka.',
  'Installed on this server, checked on {tgl}.':
    'Yang terpasang di server ini, diperiksa {tgl}.',
  'Runtime and infrastructure': 'Runtime & infrastruktur',
  Backend: 'Backend',
  Frontend: 'Frontend',
  'Build tooling': 'Perkakas build',
  Contact: 'Kontak',

  // Jeda slideshow — dipakai dashboard maupun Camera Access.
  '15 seconds': '15 detik',
  '30 seconds': '30 detik',
  '45 seconds': '45 detik',
  '1 minute': '1 menit',
  '2 minutes': '2 menit',
  '3 minutes': '3 menit',
  '5 minutes': '5 menit',

  // ——— Kamera privat ———
  Private: 'Privat',
  'Private · {nama}': 'Privat · {nama}',
  'Private camera': 'Kamera privat',
  'Only the owners can see this camera’s picture, live or recorded. Remove every owner to make it a regular camera again.':
    'Hanya pemilik yang bisa melihat gambar kamera ini, baik live maupun rekaman. Lepas semua pemilik untuk mengembalikannya jadi kamera biasa.',
  'Not private — a regular camera.': 'Bukan kamera privat — kamera biasa.',
  'Add owner…': 'Tambah pemilik…',
  'Making this camera private removes it from all dashboards and from other people’s monitors, and hides other people’s exports and bookmarks of it. Afterwards only its owners can see its picture.':
    'Menjadikan kamera ini privat akan mencabutnya dari semua dashboard dan monitor milik orang lain, serta menyembunyikan export dan bookmark orang lain untuk kamera ini. Sesudahnya hanya pemiliknya yang bisa melihat gambarnya.',
  'No users.': 'Belum ada pengguna.',
};
